import React, { forwardRef } from 'react'
import styled, { css } from 'styled-components'

import { SafeFlagImg, SafeStickerImg } from '../components/SafeImgs'

import WithForcedMonospace from '../components/WithForcedMonospace'
import WithPodiumStyles from '../components/WithPodiumStyles'

// import { ZERO_WIDTH_SPACE } from '../unicodeChars' 

import { getChapterProgressTooltipText, getFormattedDelta } from '../utils'

// import FlipMove from 'react-flip-move'

import { useStore } from '../zustandStore'

// import { accordionEnterTop, accordionLeaveBottom } from '../customFlipMoveAnimations'


const SubmissionsLargeEntry = forwardRef(({ submission, isPlaceholder }, ref) => {
  const {
    player,
    chapter,
    totalChapterCount,
    event,
    layoutSettings
  } = useStore(
    state => ({
      player: state.players.find(p => p.slug === submission.playerSlug),
      chapter: state.event.chapters[submission.chapterName],
      totalChapterCount: state.event.chapterNames.length,
      event: state.event,
      layoutSettings: state.layoutSettings
    })
  )

  const isCurrentPlayerBestTime = submission.time == player.eventBestTimes[submission.chapterName]

  const chapterProgressTooltipText = getChapterProgressTooltipText(player, event, layoutSettings)
  return (
    <StyleWrapper className='submission-wrapper' ref={ref} isPlaceholder={isPlaceholder}>
      <PlayerTitle className='player-title'>
        {player.completedAllChapters && (
          <Rank className='rank'>
            <WithForcedMonospace>{player.rank}</WithForcedMonospace>
          </Rank>
        )}
        <Name className='name'>
          {player.name}
        </Name>
        {player.isCountryPresent && (
          <Flag className='flag'>
            <SafeFlagImg {...player} />
          </Flag>
        )}
      </PlayerTitle>
      <BodyWrapper className='body-wrapper'>
        {chapter.hasSticker && (
          <div className='sticker-wrapper'>
            <SafeStickerImg stickerName={chapter.shortName} />
          </div>
        )}
        <div className='chapter-full-name info-header'>
          {chapter.fullName}
        </div>
        <div className='total-header total info-header'>
          Total
        </div>
        {submission.delta
          ? (
            <div className='delta left-cell'>
              <WithForcedMonospace>
                {getFormattedDelta(submission.delta)}
              </WithForcedMonospace>
            </div>
          ) : (
            <div title={chapterProgressTooltipText} className='chapter-progress left-cell'>
              <WithForcedMonospace>
                {player.completedChapterCount || 0}/{totalChapterCount}
              </WithForcedMonospace>
              &nbsp;complete
            </div>
          )
        }
        <div className='submitted-time'>
          {isCurrentPlayerBestTime
            ? (
              <WithPodiumStyles rank={player.eventChapterRanks[submission.chapterName]}>
                {submission.time}
              </WithPodiumStyles>
            ) : (
              <WithForcedMonospace>
                {submission.time}
              </WithForcedMonospace>
            )
          }   
        </div>

        <div className='player-total total'>
          {!player.completedAllChapters && '*'}
          <WithForcedMonospace>
            {player.eventBestTimes.total}
          </WithForcedMonospace>
        </div>
      </BodyWrapper>
    </StyleWrapper>
  )
})

export default SubmissionsLargeEntry

const StyleWrapper = styled.div`

display: grid;
  width: 96%;
  margin-left: auto;
  margin-right: auto;

  ${props => props.isPlaceholder ? css`
    opacity: 0 !important;
    color: #0000 !important;
    border: 2px solid #0000;
  ` : css`
    border: 2px solid #aaa;
    border-radius: 5px;
  `}

  font-size: 2em;
  grid-template-rows: min-content 1fr;

  white-space: nowrap;
  * {
    white-space: nowrap;
  }
`

const BodyWrapper = styled.div`
  grid-row: 2;
  grid-column: 1;
  display: grid;
  font-size: 1.5em;
  grid-template-rows: 1em 1fr;
  grid-template-columns: 13vw 1fr 1fr 1fr;

  padding-right: 10px;

  .sticker-wrapper {
    grid-row-start: 1;
    grid-row-end: none;
    grid-column: 1;
    min-width: 5vw;
    text-align: center;
    display: flex;
    justify-content: center;
    align-items: center;

    img {
      padding-top: 5px;
      padding-bottom: 5px;

      max-height: calc(100vh / 11);
      max-width: 95%;
    }
  }

  .info-header {
    font-size: 0.8em;
  }

  .chapter-full-name {
    grid-row: 1;
    grid-column-start: 2;
    grid-column-end: 4;
    /* font-size: 0.8em; */
  }

  .total-header {
    font-size: 0.65em;
    margin-top: auto;
    grid-row: 1;
    grid-column: 4;
    text-align: right;
    line-height: 1em;
  }

  .left-cell {
    grid-column: 2;
    grid-row: 2;
    text-align: center;
    &.delta {
      color: var(--time-saved-delta-color);
    }
    &.chapter-progress {
      font-size: 0.6em;
      display: flex;
      align-items: center;
      justify-content: center;
      color: var(--completed-chapter-count-color);
    }
  }

  .submitted-time {
    grid-row: 2;
    grid-column: 3;
    text-align: center;
  }

  .player-total {
    grid-row: 2;
    grid-column: 4;
    text-align: right;
  }

  .total {
    color: var(--total-color);
  }
`

const PlayerTitle = styled.div`
  grid-row: 1;
  grid-column: 1;
  display: grid;
  width: 100%;
  background-color: var(--panel-title-obs-color);
  max-width: 100%;
  ${'' /* overflow: hidden; */}
  grid-template-columns: min-content auto 1fr;
  padding-top: 0.05em;
  padding-bottom: 0.1em;
  border-top-right-radius: 3px;
  border-top-left-radius: 3px;

  font-size: 1.25em;
`

const Flag = styled.div`
  grid-row: 1;
  grid-column: 3;
  --flag-width: 55px;
  margin: auto calc(var(--flag-width) * 0.2) auto auto;

  > img {
    width: var(--flag-width);
    height: calc(var(--flag-width) * (2/3));
  }
  line-height: 1em;
`

const Name = styled.div`
  grid-row: 1;
  grid-column: 2;
  ${'' /* line-height: 1.4em; */}
  overflow-x: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  /* line-height: 0.9em; */
  margin-left: 0.2em;
  line-height: 1.35em;
`
const Rank = styled.div`
  grid-row: 1;
  grid-column: 1;
  color: var(--rank-color);
  display: flex;
  font-weight: bold;
  align-items: center;
  padding-top: 10px;
  justify-content: center;
  font-size: 0.6em;
  margin-left: 0.4em;
  ${'' /* margin-bottom: 0.1em; */}
`